/**
 * The picked case, before it runs: its input on the left, its output on the
 * right. A dataset item shows what it holds; a live dataset's trace shows the
 * observation as its source returned it. "Try one case" has no output until
 * the agent answers, so it passes its own right-hand side.
 */

import type { ReactNode } from "react";
import { SectionLabel } from "@/components/ui/box";
import { JsonView } from "@/components/ui/json-view";
import type { DatasetItem } from "@/features/datasets/types";
import { useEvaluatorSample } from "@/features/evaluators/hooks";
import { inputEntries } from "@/lib/format";

type TraceCase = { connection_id: string; trace_id: string; observation_id?: string | null };
type Doc = Record<string, unknown>;
const isDoc = (v: unknown): v is Doc => typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Input and output of a trace as its source returned it. A Langfuse observation
 * holds them itself; a qFlow run is `{run, nodes}`: the input is the run's
 * trigger event, the output each node's output by node id (as it is scored).
 */
export function traceSides(doc: Doc): { input: unknown; output: unknown } {
  if (!isDoc(doc.run) || !isDoc(doc.nodes)) return { input: doc.input, output: doc.output };
  const listed = Array.isArray(doc.nodes.nodes) ? doc.nodes.nodes.filter(isDoc) : [];
  const outputs = listed.filter((n) => isDoc(n.output)).map((n) => [String(n.node_id), n.output]);
  return {
    input: doc.run.trigger_event ?? null,
    output: outputs.length ? Object.fromEntries(outputs) : null,
  };
}

function Column({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <SectionLabel>{title}</SectionLabel>
      {children}
    </div>
  );
}

export function CaseColumns({
  input,
  output,
  outputTitle = "Output",
}: {
  input: unknown;
  output: ReactNode;
  outputTitle?: string;
}) {
  return (
    <section aria-label="Case preview" className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Column title="Input">
        <JsonView value={input} label="Case input" className="max-h-[40vh]" />
      </Column>
      <Column title={outputTitle}>{output}</Column>
    </section>
  );
}

export const itemInput = (item: DatasetItem) => Object.fromEntries(inputEntries(item.input));

/** A dataset item with a recorded output. */
export function ItemPreview({ item }: { item: DatasetItem }) {
  return (
    <CaseColumns
      input={itemInput(item)}
      output={<JsonView value={item.output} label="Case output" className="max-h-[40vh]" />}
    />
  );
}

/** A live dataset's case: fetches the observation, then shows its input and output. */
export function TracePreview({ trace }: { trace: TraceCase }) {
  const sample = useEvaluatorSample({
    connection_id: trace.connection_id,
    trace_id: trace.trace_id,
    ...(trace.observation_id && { observation_id: trace.observation_id }),
  });
  if (sample.isPending) return <p className="text-xs text-muted">Loading the trace…</p>;
  if (sample.isError || !sample.data) return <p className="text-xs text-muted">Could not load the trace.</p>;
  const { input, output } = traceSides(sample.data);
  return (
    <CaseColumns
      input={input}
      output={<JsonView value={output} label="Case output" className="max-h-[40vh]" />}
    />
  );
}
